import dayjs from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  List,
  Paper,
  Typography,
} from '@mui/material';

import React from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { mainRoute } from '@src/utils/consts/routesMainMenu.const';

import PageContainer from '@components/commons/containerPage.common';

import { useAnalytics } from '@contexts/analyticsContext';

import { scrollToTop } from '@utils';

import ProgressListItem from './_components/progressListItem';
import ReportHeader from './_components/reportHeader';
import { formatNumber, prepareChartData } from './_utils/chartHelpers';
import LocalizedThemeProvider from './_utils/localizedThemeProvider';

// Define a consistent color palette for charts
const CHART_COLORS = {
  views: '#0891b2', // Your primary color (cyan-600)
  activeUsers: '#1e88e5', // A shade of blue
  engagement: '#ff9800', // Orange
  eventCount: '#d32f2f', // Red (will be used for top events progress bar)
  pages: '#00bcd4', // Cyan (used for progress bar)
  countries: '#4caf50', // Green
  browsers: '#9c27b0', // Purple
};

// --- Component Definition ---
function ReportSnapshotPage() {
  const navigate = useNavigate();
  const { propertyId: urlPropertyId } = useParams();
  // Consume state from AnalyticsContext
  const {
    firstInit,
    setFirstInit,
    overviewInit,
    setOverviewInit,
    properties,
    selectedProperty,
    // Consume individual report data states
    timeSeriesReport,
    pagesReport,
    eventsReport,
    countriesReport,
    browsersReport,
    loadingReport, // Overall loading for all reports
    reportError, // Overall error for all reports
    loadingProperties,
    propertiesError,
    changeSelectedProperty,
    startDate,
    endDate,
  } = useAnalytics();

  React.useEffect(() => {
    if (firstInit === null) setFirstInit(true);
  }, [firstInit]);

  React.useEffect(() => {
    if (overviewInit === null) setOverviewInit(true);
  }, [overviewInit]);

  React.useEffect(() => {
    if (
      !loadingProperties &&
      properties &&
      properties.length > 0 &&
      urlPropertyId
    ) {
      const propertyFromUrl = properties.find(
        (p) => p.propertyId === urlPropertyId,
      );
      if (
        propertyFromUrl &&
        selectedProperty?.propertyId !== propertyFromUrl.propertyId
      ) {
        changeSelectedProperty(propertyFromUrl);
      } else if (urlPropertyId && !propertyFromUrl) {
        toast.error(
          `Property with ID "${urlPropertyId}" not found or accessible.`,
        );
        navigate('/analytics');
      }
    }
  }, [
    urlPropertyId,
    properties,
    loadingProperties,
    selectedProperty,
    changeSelectedProperty,
    navigate,
  ]);

  React.useEffect(() => {
    scrollToTop();
  }, []);

  const handleCreateCustomFilter = () => {
    if (selectedProperty) {
      navigate(
        `/analytics/${selectedProperty.propertyId}/detail/custom-filter/new`,
      );
    } else {
      toast.error('Please select a property first to create a custom filter.');
    }
  };

  // Helper to format date range for titles
  const formatDateRangeForTitle = (start, end) => {
    if (!start || !end) return '';
    const startObj = dayjs(start);
    const endObj = dayjs(end);

    // If it's a single day, show just that day
    if (startObj.isSame(endObj, 'day')) {
      return startObj.format('MMM D, YYYY');
    }
    // If within the same year
    if (startObj.year() === endObj.year()) {
      // If within the same month
      if (startObj.month() === endObj.month()) {
        return `${startObj.format('MMM D')} - ${endObj.format('D, YYYY')}`;
      }
      // Different months, same year
      return `${startObj.format('MMM D')} - ${endObj.format('MMM D, YYYY')}`;
    }
    // Different years
    return `${startObj.format('MMM D, YYYY')} - ${endObj.format(
      'MMM D, YYYY',
    )}`;
  };

  const currentPeriod = formatDateRangeForTitle(startDate, endDate);

  // Define breadcrumb items dynamically
  const breadcrumbItems = [
    { label: 'MISC', path: '#' },
    { label: 'ANALYTICS', path: mainRoute.MISC_ANALYTICS },
    {
      label: selectedProperty?.displayName || 'Loading Property...',
      path: selectedProperty
        ? `${mainRoute.MISC_ANALYTICS}/${selectedProperty.propertyId}`
        : undefined,
    },
  ];

  // --- Conditional Rendering based on overall state ---
  if (loadingProperties || propertiesError) {
    return (
      <PageContainer breadcrumbItems={breadcrumbItems} childrenSx={{ p: 3 }}>
        {loadingProperties && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress />
            <Typography variant='h6' sx={{ ml: 2 }}>
              Loading Google Analytics Properties...
            </Typography>
          </Box>
        )}
        {propertiesError && (
          <Alert severity='error'>
            Error loading properties: {propertiesError}
          </Alert>
        )}
      </PageContainer>
    );
  }

  // Use overall loadingReport and reportError
  if (loadingReport) {
    return (
      <PageContainer breadcrumbItems={breadcrumbItems} childrenSx={{ p: 3 }}>
        <Box maxWidth='xl' sx={{ mt: 4, mb: 4 }}>
          <Typography
            variant='h5'
            component='h2'
            gutterBottom
            sx={{ mt: 2, mb: 4 }}
          >
            Overview for {selectedProperty?.displayName || 'selected property'}
          </Typography>
          {/* Added key to ReportHeader to force re-render on property change */}
          <ReportHeader
            key={selectedProperty?.propertyId || 'no-property-selected'}
            sx={{ mr: 3 }}
          />
          <Container
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 'calc(100vh - 200px)',
            }}
          >
            <CircularProgress />
            <Typography variant='h6' sx={{ ml: 2 }}>
              Loading Report Data for{' '}
              {selectedProperty?.displayName || 'selected property'}...
            </Typography>
          </Container>
        </Box>
      </PageContainer>
    );
  }

  if (reportError) {
    return (
      <PageContainer breadcrumbItems={breadcrumbItems} childrenSx={{ p: 3 }}>
        <Box maxWidth='xl' sx={{ mt: 4, mb: 4 }}>
          <Typography
            variant='h5'
            component='h2'
            gutterBottom
            sx={{ mt: 2, mb: 4 }}
          >
            Overview for {selectedProperty?.displayName || 'selected property'}
          </Typography>
          {/* Added key to ReportHeader to force re-render on property change */}
          <ReportHeader
            key={selectedProperty?.propertyId || 'no-property-selected'}
            sx={{ mr: 3 }}
          />
          <Container sx={{ mt: 4 }}>
            <Alert severity='error'>Error loading report: {reportError}</Alert>
          </Container>
        </Box>
      </PageContainer>
    );
  }

  if (
    !selectedProperty ||
    !timeSeriesReport ||
    !pagesReport ||
    !eventsReport ||
    !countriesReport ||
    !browsersReport
  ) {
    return (
      <PageContainer breadcrumbItems={breadcrumbItems} childrenSx={{ p: 3 }}>
        <Box maxWidth='xl' sx={{ mt: 4, mb: 4 }}>
          <Typography
            variant='h5'
            component='h2'
            gutterBottom
            sx={{ mt: 2, mb: 4 }}
          >
            Overview for {selectedProperty?.displayName || 'selected property'}
          </Typography>
          {/* Added key to ReportHeader to force re-render on property change */}
          <ReportHeader
            key={selectedProperty?.propertyId || 'no-property-selected'}
            sx={{ mr: 3 }}
          />
          <Container sx={{ mt: 4 }}>
            <Alert severity='info'>
              No Google Analytics property selected or some report data is not
              available. Please ensure a property is selected and try
              refreshing.
            </Alert>
          </Container>
        </Box>
      </PageContainer>
    );
  }

  // --- Data preparation for charts (using imported helpers) ---
  // Use data from timeSeriesReport
  const viewsOverTimeData = prepareChartData(
    timeSeriesReport.rows,
    'date',
    'screenPageViews',
  );
  const activeUsersOverTimeData = prepareChartData(
    timeSeriesReport.rows,
    'date',
    'activeUsers',
  );
  const averageEngagementTimeData = prepareChartData(
    timeSeriesReport.rows,
    'date',
    'userEngagementDuration',
    'activeUsers',
  );
  const eventCountOverTimeData = prepareChartData(
    // This is still time-series event count
    timeSeriesReport.rows,
    'date',
    'eventCount',
  );

  // Use data from specific reports
  const topEventsData = prepareChartData(
    eventsReport.rows,
    'eventName',
    'eventCount',
  );
  const topPagesData = prepareChartData(
    pagesReport.rows,
    'unifiedPagePathScreen', // This will trigger pageTitle/screenClass logic in chartHelpers
    'screenPageViews',
  );
  const topCountriesData = prepareChartData(
    countriesReport.rows,
    'country',
    'activeUsers',
  );
  const topBrowsersData = prepareChartData(
    browsersReport.rows,
    'browser',
    'activeUsers',
  );

  // Calculate summary metrics from timeSeriesReport
  const totalScreenPageViews = timeSeriesReport.rows.reduce(
    (sum, row) => sum + parseFloat(row.screenPageViews || 0),
    0,
  );
  const totalActiveUsers = timeSeriesReport.rows.reduce(
    (sum, row) => sum + parseFloat(row.activeUsers || 0),
    0,
  );
  const totalUserEngagementDuration = timeSeriesReport.rows.reduce(
    (sum, row) => sum + parseFloat(row.userEngagementDuration || 0),
    0,
  );
  const totalEventCount = timeSeriesReport.rows.reduce(
    (sum, row) => sum + parseFloat(row.eventCount || 0),
    0,
  );

  // Average Engagement Time per User (overall)
  const avgEngagementPerUser =
    totalActiveUsers > 0 ? totalUserEngagementDuration / totalActiveUsers : 0;

  // Calculate max page views for percentage calculation in ProgressListItem
  const maxPageViews =
    topPagesData.length > 0
      ? Math.max(...topPagesData.map((item) => item.value))
      : 1;

  // Calculate max event count for percentage calculation in ProgressListItem
  const maxEventCount =
    topEventsData.length > 0
      ? Math.max(...topEventsData.map((item) => item.value))
      : 1;

  return (
    <LocalizedThemeProvider>
      <PageContainer breadcrumbItems={breadcrumbItems} childrenSx={{ p: 3 }}>
        {/* The main content box. Removed mt here to rely on PageContainer's padding. */}
        <Box maxWidth='xl' sx={{ mb: 4 }}>
          <Typography
            variant='h5'
            component='h2'
            gutterBottom
            sx={{ mt: 2, mb: 4 }}
          >
            Overview for {selectedProperty.displayName}
          </Typography>

          {/* ReportHeader has its own mb: 3 */}
          <ReportHeader
            key={selectedProperty?.propertyId || 'no-property-selected'}
            sx={{ mr: 3 }}
          />

          {/* Summary Metric Cards */}
          {/* Added mt to create space from ReportHeader's mb */}
          <Box sx={{ mt: 3, mr: 3 }}>
            <Grid container spacing={4}>
              <Grid item sx={{ mt: 4 }} xs={12} sm={6} md={3}>
                <Paper
                  elevation={3}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    p: 2,
                    textAlign: 'center',
                    height: '100%',
                  }}
                >
                  <Typography
                    variant='subtitle1'
                    color='text.secondary'
                    fontWeight='bold'
                    sx={{ mt: 2 }}
                    gutterBottom
                  >
                    Total Views
                  </Typography>
                  <Typography
                    variant='h4'
                    fontWeight='bold'
                    color={CHART_COLORS.views}
                    gutterBottom
                  >
                    {formatNumber(totalScreenPageViews)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item sx={{ mt: 4 }} xs={12} sm={6} md={3}>
                <Paper
                  elevation={3}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    p: 2,
                    textAlign: 'center',
                    height: '100%',
                  }}
                >
                  <Typography
                    variant='subtitle1'
                    color='text.secondary'
                    fontWeight='bold'
                    sx={{ mt: 2 }}
                    gutterBottom
                  >
                    Total Active Users
                  </Typography>
                  <Typography
                    variant='h4'
                    fontWeight='bold'
                    color={CHART_COLORS.activeUsers}
                    gutterBottom
                  >
                    {formatNumber(totalActiveUsers)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item sx={{ mt: 4 }} xs={12} sm={6} md={3}>
                <Paper
                  elevation={3}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    p: 2,
                    textAlign: 'center',
                    height: '100%',
                  }}
                >
                  <Typography
                    variant='subtitle1'
                    color='text.secondary'
                    fontWeight='bold'
                    sx={{ mt: 2 }}
                    gutterBottom
                  >
                    Avg. Engagement Time (s)
                  </Typography>
                  <Typography
                    variant='h4'
                    fontWeight='bold'
                    color={CHART_COLORS.engagement}
                    gutterBottom
                  >
                    {avgEngagementPerUser.toFixed(1)}s
                  </Typography>
                </Paper>
              </Grid>
              <Grid item sx={{ mt: 4 }} xs={12} sm={6} md={3}>
                <Paper
                  elevation={3}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    p: 2,
                    textAlign: 'center',
                    height: '100%',
                  }}
                >
                  <Typography
                    variant='subtitle1'
                    color='text.secondary'
                    fontWeight='bold'
                    sx={{ mt: 2 }}
                    gutterBottom
                  >
                    Total Events
                  </Typography>
                  <Typography
                    variant='h4'
                    fontWeight='bold'
                    color={CHART_COLORS.eventCount}
                    gutterBottom
                  >
                    {formatNumber(totalEventCount)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          {/* Main Panels */}
          {/* Added mt to create space from Summary Metric Cards */}
          <Box sx={{ mt: 4, mr: 3 }}>
            <Grid container spacing={4}>
              {/* Panel 1: Views Over Time */}
              <Grid item sx={{ mt: 6 }} xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                  <Typography variant='h6' gutterBottom>
                    Views ({currentPeriod})
                  </Typography>
                  <ResponsiveContainer width='100%' height={300}>
                    <LineChart data={viewsOverTimeData}>
                      <CartesianGrid strokeDasharray='3 3' />
                      <XAxis dataKey='date' />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type='monotone'
                        dataKey='screenPageViews'
                        stroke={CHART_COLORS.views}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Panel 2: Active Users Over Time */}
              <Grid item sx={{ mt: 6 }} xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                  <Typography variant='h6' gutterBottom>
                    Active Users ({currentPeriod})
                  </Typography>
                  <ResponsiveContainer width='100%' height={300}>
                    <LineChart data={activeUsersOverTimeData}>
                      <CartesianGrid strokeDasharray='3 3' />
                      <XAxis dataKey='date' />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type='monotone'
                        dataKey='activeUsers'
                        stroke={CHART_COLORS.activeUsers}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Panel 3: Average Engagement Time Over Time */}
              <Grid item sx={{ mt: 6 }} xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                  <Typography variant='h6' gutterBottom>
                    Average Engagement Time (s) ({currentPeriod})
                  </Typography>
                  <ResponsiveContainer width='100%' height={300}>
                    <LineChart data={averageEngagementTimeData}>
                      <CartesianGrid strokeDasharray='3 3' />
                      <XAxis dataKey='date' />
                      <YAxis />
                      <Tooltip formatter={(value) => `${value.toFixed(2)}s`} />
                      <Legend />
                      <Line
                        type='monotone'
                        dataKey='userEngagementDuration'
                        stroke={CHART_COLORS.engagement}
                        activeDot={{ r: 8 }}
                        name='Avg. Engagement Time'
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Panel 4: Top Events by Name (now using ProgressListItem) */}
              <Grid item sx={{ mt: 6 }} xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                  <Typography variant='h6' gutterBottom>
                    Top Events (Count) ({currentPeriod})
                  </Typography>
                  <List sx={{ pt: 0 }}>
                    {topEventsData.slice(0, 10).map((item) => (
                      <ProgressListItem
                        key={item.name}
                        label={item.name}
                        value={item.value}
                        percentage={(item.value / maxEventCount) * 100}
                        color='secondary'
                      />
                    ))}
                  </List>

                  <Button
                    variant='text'
                    className='text-[math-auto]'
                    sx={{ mt: 2 }}
                    onClick={() =>
                      navigate(
                        `/analytics/${selectedProperty.propertyId}/detail/events`,
                      )
                    }
                  >
                    See more: Events
                  </Button>
                </Paper>
              </Grid>

              {/* Panel 5: Top Pages and Screens (using ProgressListItem) */}
              <Grid item sx={{ mt: 6 }} xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                  <Typography variant='h6' gutterBottom>
                    Views by Page title and screen class ({currentPeriod})
                  </Typography>
                  <List sx={{ pt: 0 }}>
                    {topPagesData.slice(0, 10).map((item) => (
                      <ProgressListItem
                        key={item.name}
                        label={item.name}
                        value={item.value}
                        percentage={(item.value / maxPageViews) * 100}
                        color='primary'
                      />
                    ))}
                  </List>
                  <Button
                    variant='text'
                    className='text-[math-auto]'
                    sx={{ mt: 2 }}
                    onClick={() =>
                      navigate(
                        `/analytics/${selectedProperty.propertyId}/detail/pages-and-screens`,
                      )
                    }
                  >
                    See more: Pages and Screens
                  </Button>
                </Paper>
              </Grid>

              {/* Panel 6: Top Countries by Active Users */}
              <Grid item sx={{ mt: 6 }} xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                  <Typography variant='h6' gutterBottom>
                    Top Countries (Active Users) ({currentPeriod})
                  </Typography>
                  <ResponsiveContainer width='100%' height={300}>
                    <BarChart
                      data={topCountriesData.slice(0, 10)}
                      layout='vertical'
                    >
                      <CartesianGrid strokeDasharray='3 3' />
                      <XAxis type='number' />
                      <YAxis type='category' dataKey='name' width={100} />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey='value'
                        fill={CHART_COLORS.countries}
                        name='Active Users'
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Panel 7: Top Browsers by Active Users */}
              <Grid item sx={{ mt: 6 }} xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                  <Typography variant='h6' gutterBottom>
                    Top Browsers (Active Users) ({currentPeriod})
                  </Typography>
                  <ResponsiveContainer width='100%' height={300}>
                    <BarChart
                      data={topBrowsersData.slice(0, 10)}
                      layout='vertical'
                    >
                      <CartesianGrid strokeDasharray='3 3' />
                      <XAxis type='number' />
                      <YAxis type='category' dataKey='name' width={100} />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey='value'
                        fill={CHART_COLORS.browsers}
                        name='Active Users'
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Panel 8: Add new custom filter */}
              <Grid item sx={{ mt: 6 }} xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    height: '99%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    border: '2px dashed',
                    borderColor: 'divider',
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                  }}
                  onClick={handleCreateCustomFilter}
                >
                  <AddCircleOutlineIcon
                    sx={{ fontSize: 60, color: 'text.secondary' }}
                  />
                  <Typography
                    variant='h6'
                    color='text.secondary'
                    sx={{ mt: 2 }}
                  >
                    Create a New Custom Filter
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </PageContainer>
    </LocalizedThemeProvider>
  );
}

export default ReportSnapshotPage;
